/**
 * Utility to format, sanitize, and extract S3 Photo URLs across HumbleBee Web App
 * Preserves exact raw S3 object keys as uploaded to AWS S3
 */

// 1. Raw S3 Photo URL Handler - Preserves exact S3 Object Key
export const sanitizeS3PhotoUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  const url = rawUrl.trim();
  if (!url.startsWith('http://') && !url.startsWith('https://')) return '';
  // Return exact raw URL as stored in S3 / DB
  return url;
};

// Deep Recursive S3 Photo & Media URL Extractor for a Lead / Candidate
export const extractAllCandidateS3Urls = (obj, foundUrls = new Set(), depth = 0) => {
  if (!obj || depth > 8) return Array.from(foundUrls);

  if (typeof obj === 'string') {
    const trimmed = obj.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const cleaned = sanitizeS3PhotoUrl(trimmed);
      if (cleaned) foundUrls.add(cleaned);
    } else if (trimmed.includes(',')) {
      trimmed.split(',').forEach(part => {
        const cleaned = sanitizeS3PhotoUrl(part);
        if (cleaned) foundUrls.add(cleaned);
      });
    }
  } else if (Array.isArray(obj)) {
    obj.forEach(item => extractAllCandidateS3Urls(item, foundUrls, depth + 1));
  } else if (typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      if (key !== 'projectMediaMap' && key !== 'clusters') {
        extractAllCandidateS3Urls(obj[key], foundUrls, depth + 1);
      }
    });
  }

  return Array.from(foundUrls);
};

// 2. Extract Candidate Profile Photo S3 URL (Pattern A)
export const getCandidateProfilePhotoUrl = (lead) => {
  if (!lead) return '-';
  const raw = lead.photo_url ||
    lead.userInfo?.photo_url ||
    lead.profile_photo ||
    lead.basicProfile?.photo_url ||
    lead.surveyData?.stages?.[0]?.form_data?.photo_url ||
    lead.fieldViewData?.[0]?.photo_url;

  if (raw) {
    const cleaned = sanitizeS3PhotoUrl(raw);
    if (cleaned) return cleaned;
  }

  // Fallback: Use candidate's first extracted S3 photo URL
  const allUrls = extractAllCandidateS3Urls(lead);
  return allUrls.length > 0 ? allUrls[0] : '-';
};

// 3. Extract Candidate Survey & Document Photos S3 URLs (Pattern B)
export const getCandidateSurveyPhotosUrls = (lead) => {
  if (!lead) return '-';
  const allUrls = extractAllCandidateS3Urls(lead);
  const profilePhoto = getCandidateProfilePhotoUrl(lead);

  const surveyUrls = allUrls.filter(u => u !== profilePhoto);
  return surveyUrls.length > 0 ? surveyUrls.join(' | ') : (allUrls.length > 1 ? allUrls.slice(1).join(' | ') : '-');
};

// 4. All Candidate Photo S3 Links (Combined Profile + Survey)
export const getCandidateAllPhotoUrls = (lead) => {
  if (!lead) return '-';
  const allUrls = extractAllCandidateS3Urls(lead);
  return allUrls.length > 0 ? allUrls.join(' | ') : '-';
};
